// Libs
import { compact } from "lodash";
import type React from "react";
import { useLocation } from "react-router-dom";

// Utils
import { type TFunction, useTranslation } from "src/design-system/core/TranslationContext";
import { useAuth } from "src/hooks";
import { SHARED_LAYOUT_NAMESPACE, TOP_BAR_TK } from "src/layouts/SharedLayout/config";
import { isAdminUser } from "src/utils/permission";

// Components
import { List } from "@mui/material";
import {
    PATH_AGENDA,
    PATH_OFFER,
    PATH_SPONSORED_ADS,
    PATH_SUPERADMIN,
    PATH_SUPERADMIN_ANALYZE,
    PATH_SUPERADMIN_DEBUG,
    PATH_SUPERADMIN_MONITORING,
    PATH_SUPERADMIN_SUPERPOWER,
} from "src/routesConstants";
import { isPageAllowed } from "src/utils/pageAccess";
import { VITE_ACTIVATE_USER_ID_SUPERPOWER_TAB } from "src/views/admin/AdminView/admin-superpower/config";
import { PATH_VISIBILITY } from "src/views/ranking/routes";
import NavItem from "./MenuItem";

interface Item {
    href?: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
    items?: Item[];
    title: string;
}

function renderNavItems({ items, pathname, t }: { items: Item[]; pathname: string; t: TFunction }) {
    return (
        <List style={{ display: "flex" }}>
            {items.reduce((acc, item) => reduceChildRoutes({ acc, item, pathname, t }), [])}
        </List>
    );
}

function reduceChildRoutes({
    acc,
    pathname,
    item,
    t,
}: {
    acc: any[];
    pathname: string;
    item: Item;
    t: TFunction;
}) {
    const key = item.title;
    if (item.items) {
        acc.push(
            <NavItem info={item.info} key={key} title={t(item.title)}>
                {renderNavItems({
                    pathname,
                    items: item.items,
                    t,
                })}
            </NavItem>
        );
    } else {
        acc.push(<NavItem href={item.href} info={item.info} key={key} title={t(item.title)} />);
    }

    return acc;
}

function MenuBar(): JSX.Element {
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useTranslation(`${SHARED_LAYOUT_NAMESPACE}:${TOP_BAR_TK}`);
    const activateSuperpowerUserIdList = VITE_ACTIVATE_USER_ID_SUPERPOWER_TAB
        ? VITE_ACTIVATE_USER_ID_SUPERPOWER_TAB?.split(";")
        : [];

    const items: Item[] = [
        {
            title: "modules.agenda",
            href: PATH_AGENDA,
        },
        {
            title: "modules.offers",
            href: PATH_OFFER,
        },
        {
            title: "modules.sponsored-ads",
            href: PATH_SPONSORED_ADS,
        },
        {
            title: "modules.visibility",
            href: PATH_VISIBILITY,
        },
    ]
        .filter(Boolean)
        .filter((item) => isPageAllowed({ user, path: item.href }));

    const itemsAdmin: Item[] = compact([
        {
            title: "superadmin",
            href: PATH_SUPERADMIN,
        },
        {
            title: "monitoring",
            href: `${PATH_SUPERADMIN_MONITORING}/dashboard`,
        },
        {
            title: "analyze",
            href: `${PATH_SUPERADMIN_ANALYZE}/dashboard`,
        },
        {
            title: "admin-debug",
            href: `${PATH_SUPERADMIN_DEBUG}/dashboard`,
        },
        activateSuperpowerUserIdList.includes(user.id) && {
            title: "superpower",
            href: `${PATH_SUPERADMIN_SUPERPOWER}/dashboard`,
        },
    ]);

    return (
        <div style={{ display: "flex" }}>
            {renderNavItems({
                items: isAdminUser(user) ? itemsAdmin : items,
                pathname: location.pathname,
                t,
            })}
        </div>
    );
}

export default MenuBar;
